import { useContext, useState, useRef } from "react";
import { stringQueries } from "../../../../../config/filter/CustomFilterConfig";
import {
  FirmaFiltersContext,
  FirmaFiltersEventContext,
} from "../../../../../context/FiltersContext";
import {
  QueryFilter,
  FirmaFilterKeys,
  FirmaPostnummerFilterSetting,
} from "../../../../../interfaces/filters";
import { FirmaDtoFilterInput } from "../../../../../interfaces/schema";
import { f } from "../../../../../util/f";
import { AccordionSubFilterItem } from "../../../../accordion/Accordion";
import AppliedFilterItem from "../../../../filter/AppliedFilterItem";
import { ComboBox } from "../../../../filter/ComboBox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../../select/Select";

const filterKey: QueryFilter<FirmaFilterKeys>["filterKey"] = "postnummer";

export default function FirmaPostnummerFilter() {
  // query filter context
  const filters = useContext(FirmaFiltersContext);
  const updateFilters = useContext(FirmaFiltersEventContext);

  // find the filter settings
  const settings: FirmaPostnummerFilterSetting[] =
    filters.find((f) => f.filterKey === filterKey)?.settings || [];

  // preppings
  const queries = stringQueries;

  // state things
  const [selectedQuery, setSelectedQuery] = useState(queries[0]);

  // refs
  const inputRef = useRef<HTMLInputElement>(null);

  // handlers
  function inputEventHandler() {
    const inputValue = inputRef.current?.value;
    if (!inputValue) {
      return;
    }

    const key: FirmaPostnummerFilterSetting["key"] = `${selectedQuery.query}-${inputValue}`;

    if (settings.some((set) => set.key === key)) {
      alert("Filter er allerede aktivt.");
      return;
    }

    const settingsItem: FirmaPostnummerFilterSetting = {
      key: key,
      selectedQueryName: `${selectedQuery.displayName}`,
      displayName: `${inputValue}`,
      filter: {
        beliggenhedsadresse: {
          postnummer: {
            [selectedQuery.query]: inputValue,
          },
        },
      },
    };

    const updatedSettings = [...settings, settingsItem];

    const filterString = f<FirmaDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => set.filter),
      )
      .build();

    // update the query filter
    const queryFilter: QueryFilter<
      FirmaFilterKeys,
      FirmaPostnummerFilterSetting[]
    > = {
      filterKey: filterKey,
      filter: filterString,
      parentFilter: "and",
      settings: updatedSettings,
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterEventHandler(key: FirmaPostnummerFilterSetting["key"]) {
    const updatedSettings = settings.filter((set) => set.key !== key);

    if (updatedSettings.length === 0) {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
      return;
    }

    const filterString = f<FirmaDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => set.filter),
      )
      .build();

    // update the query filter
    const queryFilter: QueryFilter<
      FirmaFilterKeys,
      FirmaPostnummerFilterSetting[]
    > = {
      filterKey: filterKey,
      filter: filterString,
      parentFilter: "and",
      settings: updatedSettings,
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  return (
    <AccordionSubFilterItem
      title="Postnummer"
      value="postnummer"
      isActive={!!settings.length}
    >
      <div className="c-applied-filter-group">
        {settings.map((set: FirmaPostnummerFilterSetting) => {
          return (
            <AppliedFilterItem
              key={set.key}
              labelPrefix={set.selectedQueryName}
              label={set.displayName}
              removeFilterHandler={() => {
                removeFilterEventHandler(set.key);
              }}
            />
          );
        })}
      </div>

      <ComboBox>
        <Select
          defaultValue={selectedQuery?.query}
          onValueChange={(value: string) => {
            const query = queries.find((q) => q.query === value);
            if (query) {
              setSelectedQuery(query);
            }
          }}
        >
          <SelectTrigger placeholder="Vælg query type" badge />
          <SelectContent small>
            {queries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        <input
          className="form-control"
          placeholder={"Indtast postnummer ..."}
          ref={inputRef}
          type={"text"}
          required
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              inputEventHandler();
            }
          }}
        />

        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1 btn--combo-action`}
          onClick={() => {
            inputEventHandler();
          }}
        >
          OK
        </button>
      </ComboBox>
    </AccordionSubFilterItem>
  );
}
